import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabase";
import { useToast } from '@/components/base/Toast';
import Logo from '@/components/feature/Logo';
import { markUserLoginTime } from '@/components/feature/AuthGuard';

// 将手机号映射为内部登录邮箱（Supabase 邮箱+密码模式的兼容方案）
const toAuthEmail = (phone: string) => `u${phone}@jinan-gold.com`;

const langMap: Record<string, string> = {
  zh: '简体中文',
  en: 'English',
  'zh-Hant': '繁體中文',
};

const langList = [
  { label: '简体中文', value: 'zh' },
  { label: 'English', value: 'en' },
  { label: '繁體中文', value: 'zh-Hant' },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['common']);
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [errors, setErrors] = useState<{ phone?: string; password?: string }>({});
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  const currentLangLabel = langMap[i18n.language] || '简体中文';

  // 已登录则直接进入首页
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        navigate("/home", { replace: true });
      }
    });
  }, [navigate]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setLangOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const validate = () => {
    const newErrors: { phone?: string; password?: string } = {};
    if (!phone.trim()) {
      newErrors.phone = t('common:errorPhoneEmpty');
    } else if (!/^1[3-9]\d{9}$/.test(phone.trim())) {
      newErrors.phone = t('common:errorPhoneFormat');
    }
    if (!password.trim()) {
      newErrors.password = t('common:errorPasswordEmpty');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setFormError("");

    const { error } = await supabase.auth.signInWithPassword({
      email: toAuthEmail(phone.trim()),
      password,
    });

    setIsSubmitting(false);

    if (error) {
      const msg = error.message.toLowerCase();
      if (msg.includes("invalid login credentials")) {
        setFormError(t('common:errorInvalidCredentials'));
      } else if (msg.includes("email not confirmed")) {
        setFormError(t('common:errorEmailNotConfirmed'));
      } else {
        setFormError(t('common:errorLoginFailed', { message: error.message }));
      }
      return;
    }

    markUserLoginTime();
    toast.showToast(t('common:loginSuccess'));
    navigate("/home", { replace: true });
  };

  const selectLang = (value: string) => {
    setLangOpen(false);
    i18n.changeLanguage(value);
  };

  const canSubmit = phone.trim().length > 0 && password.trim().length > 0;

  return (
    <div className="min-h-screen w-full bg-brown-50 flex flex-col items-center relative">
      {/* Top Controls */}
      <div className="absolute top-4 right-4 md:top-6 md:right-8 flex items-center gap-3 z-20">
        <div className="relative" ref={langRef}>
          <button
            type="button"
            onClick={() => setLangOpen(!langOpen)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-brown-200 text-sm text-brown-700 hover:bg-brown-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-global-line text-base"></i>
            </span>
            <span>{currentLangLabel}</span>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className={`ri-arrow-down-s-line text-sm transition-transform ${langOpen ? "rotate-180" : ""}`}></i>
            </span>
          </button>
          {langOpen && (
            <div className="absolute top-full right-0 mt-2 w-44 bg-white border border-brown-200 rounded-xl shadow-lg overflow-hidden z-30">
              {langList.map((lang) => (
                <button
                  key={lang.value}
                  type="button"
                  onClick={() => selectLang(lang.value)}
                  className={`w-full text-left px-4 py-3 text-sm cursor-pointer transition-colors whitespace-nowrap ${
                    i18n.language === lang.value
                      ? "bg-blue-50 text-blue-700 font-medium"
                      : "text-brown-700 hover:bg-brown-50"
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Content Card */}
      <div className="w-full max-w-[420px] px-4 pt-20 pb-12 md:pt-24 md:pb-16">
        <div className="bg-white rounded-2xl shadow-sm border border-brown-100 px-6 py-8 md:px-8 md:py-10">
          {/* Brand */}
          <div className="flex justify-center">
            <img
              src="https://public.readdy.ai/ai/img_res/3dc270e4-5b61-4676-ac46-8193427c09c6.png"
              alt="CJJY"
              className="h-24 w-auto object-contain max-w-full"
            />
          </div>

          <h2 className="text-2xl font-bold text-brown-900 mt-6 mb-2 text-center">{t('common:welcomeLogin')}</h2>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
            {/* Phone Input */}
            <div className="relative">
              <div
                className={`flex items-center border rounded-lg px-4 py-3 transition-all duration-200 ${
                  errors.phone
                    ? "border-red-400 bg-red-50/30"
                    : "border-brown-200 bg-white hover:border-brown-300 focus-within:border-gold-400 focus-within:ring-2 focus-within:ring-gold-400/20"
                }`}
              >
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={11}
                  value={phone}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "");
                    setPhone(val);
                    if (errors.phone) setErrors((prev) => ({ ...prev, phone: undefined }));
                  }}
                  placeholder={t('common:placeholderPhone')}
                  className="w-full text-sm text-brown-900 placeholder:text-brown-400 bg-transparent outline-none"
                />
              </div>
              {errors.phone && (
                <p className="text-xs text-red-500 mt-1.5 ml-1">{errors.phone}</p>
              )}
            </div>

            {/* Password Input */}
            <div className="relative">
              <div
                className={`flex items-center border rounded-lg px-4 py-3 transition-all duration-200 ${
                  errors.password
                    ? "border-red-400 bg-red-50/30"
                    : "border-brown-200 bg-white hover:border-brown-300 focus-within:border-gold-400 focus-within:ring-2 focus-within:ring-gold-400/20"
                }`}
              >
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) setErrors((prev) => ({ ...prev, password: undefined }));
                  }}
                  placeholder={t('common:placeholderPassword')}
                  className="w-full text-sm text-brown-900 placeholder:text-brown-400 bg-transparent outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="ml-2 w-5 h-5 flex items-center justify-center text-brown-400 hover:text-brown-600 transition-colors cursor-pointer flex-shrink-0"
                  tabIndex={-1}
                >
                  <i className={`${showPassword ? "ri-eye-line" : "ri-eye-off-line"} text-lg`}></i>
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-red-500 mt-1.5 ml-1">{errors.password}</p>
              )}
            </div>

            {/* Forgot Password */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setFormError(t('common:resetPasswordHint'))}
                className="text-sm text-gold-600 hover:text-gold-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('common:forgotPassword')}
              </button>
            </div>

            {/* Form Error */}
            {formError && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5">
                <span className="w-4 h-4 flex items-center justify-center text-red-500 flex-shrink-0">
                  <i className="ri-error-warning-line text-sm"></i>
                </span>
                <span className="text-xs text-red-600">{formError}</span>
              </div>
            )}

            {/* Login Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-3 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                canSubmit
                  ? "bg-gold-500 hover:bg-gold-600 text-brown-950 shadow-sm"
                  : "bg-brown-100 text-brown-400 cursor-not-allowed"
              }`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-brown-950/30 border-t-brown-950 rounded-full animate-spin"></span>
                  {t('common:loading')}
                </span>
              ) : (
                t('common:login')
              )}
            </button>

            {/* Register Link */}
            <div className="text-center pt-2">
              <span className="text-sm text-brown-500">{t('common:noAccount')}</span>
              <button
                type="button"
                onClick={() => navigate("/register")}
                className="ml-1.5 text-sm text-gold-600 hover:text-gold-700 transition-colors cursor-pointer whitespace-nowrap font-medium"
              >
                {t('common:registerNow')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}